import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import AppShell from "../../../components/layout/AppShell";
import { getInvoiceById, getUser } from "../../../services/api";
import InvoiceDocLink from "../../../components/ui/InvoiceDocLink";
import { formatAmount, getMaxDecimalsForCurrency } from "../../../utils";


const formatDate = (iso) =>
  iso
    ? new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })
    : "—";


const InvoiceDetail = () => {
  const { id } = useParams();

  const [invoice, setInvoice] = useState(null);
  const [senderName, setSenderName] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    setLoading(true);
    getInvoiceById(id)
      .then(async (res) => {
        const inv = res.data;
        if (!inv) { setError(true); return; }
        setInvoice(inv);

        // Resolve sender display name (toUserId = the sender who pays)
        if (inv.toUserId) {
          getUser(inv.toUserId)
            .then((u) => setSenderName(u?.data?.name || null))
            .catch(() => {});
        }
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <AppShell>
        <div className="animate-pulse space-y-4">
          <div className="h-5 bg-gray-200 rounded w-48 mb-6" />
          <div className="h-8 bg-gray-200 rounded w-96 mb-4" />
          <div className="grid grid-cols-2 gap-6">
            <div className="h-80 bg-gray-200 rounded-xl" />
            <div className="h-80 bg-gray-200 rounded-xl" />
          </div>
        </div>
      </AppShell>
    );
  }

  if (error || !invoice) {
    return (
      <AppShell>
        <p className="text-gray-500">Invoice not found.</p>
      </AppShell>
    );
  }

  const shortId = (s) => (s ? s.slice(0, 8).toUpperCase() : "—");

  return (
    <AppShell>
      {/* ── Header card ──────────────────────────────────────────────────── */}
      <div className="bg-white border border-gray-200 rounded-xl px-6 py-5 mb-6">
        <h1 className="text-xl font-bold text-gray-900 font-mono">
          {invoice.invoiceId || shortId(invoice.id)}
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          {senderName || shortId(invoice.toUserId)}
        </p>
      </div>

      <p className="text-base font-semibold text-gray-900 mb-4">Invoice Details</p>

      <div className="max-w-[640px]">
        {/* ── Invoice details ──────────────────────────────────────────────── */}
        <div className="bg-white border border-gray-200 rounded-xl p-6">
          {/* ID + date row */}
          <div className="flex items-start justify-between mb-4 pb-4 border-b border-gray-100">
            <div>
              <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">Invoice ID</p>
              <p className="text-sm font-bold text-gray-900 font-mono">{invoice.invoiceId || "—"}</p>
            </div>
            <p className="text-sm text-gray-500 whitespace-nowrap ml-4">
              {formatDate(invoice.createdAt)}
            </p>
          </div>

          {/* Detail rows */}
          <div className="space-y-4">
            <div className="border-b border-gray-100 pb-4">
              <p className="text-xs text-gray-400 mb-0.5">Sender</p>
              <p className="text-sm font-medium text-gray-900">
                {senderName || shortId(invoice.toUserId)}
              </p>
            </div>

            <div className="border-b border-gray-100 pb-4">
              <p className="text-xs text-gray-400 mb-0.5">Due Date</p>
              <p className="text-sm font-medium text-gray-900">
                {invoice.dueDate ? formatDate(invoice.dueDate) : "—"}
              </p>
            </div>

            {invoice.comment && (
              <div className="border-b border-gray-100 pb-4">
                <p className="text-xs text-gray-400 mb-0.5">Description</p>
                <p className="text-sm font-medium text-gray-900">{invoice.comment}</p>
              </div>
            )}

            <div className="pb-2">
              <p className="text-xs text-gray-400 mb-0.5">Document</p>
              {invoice.docUrl ? (
                <InvoiceDocLink invoiceId={invoice.id} docUrl={invoice.docUrl} />
              ) : (
                <p className="text-sm font-medium text-gray-400">Not attached</p>
              )}
            </div>

          </div>

          {/* Amount banner */}
          <div className="mt-5 bg-green-50 rounded-xl px-4 py-4">
            <p className="text-xs font-bold text-green-600 uppercase tracking-wider mb-1">
              You Receive
            </p>
            <p className="text-3xl font-bold text-green-600">
              ${formatAmount(invoice.amount,getMaxDecimalsForCurrency("USD"))}
            </p>
          </div>
        </div>

      </div>
    </AppShell>
  );
};

export default InvoiceDetail;
